import {select, color, range} from "d3"
import { comparer, reaction } from "mobx"
import {forwardRef, MutableRefObject, useCallback, useEffect, useRef} from "react"
import RTreeLib from "rtree"
import {useMemo} from "use-memo-one"
import { isKeyDown } from "../../../hooks/use-key-states"
import {appState} from "../../../models/app-state"
import {Logger} from "../../../lib/logger"
import {IDataSet} from "../../../models/data/data-set"
import {selectAllCases, selectAndDeselectCases} from "../../../models/data/data-set-utils"
import { getTileModel } from "../../../models/tiles/tile-model"
import {defaultBackgroundColor} from "../../../utilities/color-utils"
import { mstReaction } from "../../../utilities/mst-reaction"
import { useGraphLayoutContext } from "../../graph/hooks/use-graph-layout-context"
import { kZoomInFactor, kZoomOutFactor, zoomAxis } from "../../axis/axis-utils"
import { isAnyNumericAxisModel } from "../../axis/models/numeric-axis-models"
import {rTreeRect} from "../data-display-types"
import {CaseObject, getCasesForDelta, rectNormalize, RTree} from "../data-display-utils"
import {useDataDisplayLayout} from "../hooks/use-data-display-layout"
import {useDataDisplayModelContext} from "../hooks/use-data-display-model"
import {useRendererPointerDownDeselect} from "../hooks/use-renderer-pointer-down-deselect"
import {MarqueeState} from "../models/marquee-state"
import { BackgroundPassThroughEvent, PointRendererArray } from "../renderer"

interface IProps {
  marqueeState: MarqueeState
  rendererArray: PointRendererArray
}

interface SelectionSpec {
  dataset: IDataSet
  caseIDsToSelect: string[]
  caseIDsToDeselect: string[]
}

interface SelectionMap {
  [key: string]: SelectionSpec
}

const prepareTree = (rendererArray: PointRendererArray): RTree => {
  const selectionTree = RTreeLib(10)
  rendererArray.forEach(renderer => {
    // forEachPoint provides point metadata which includes x/y coordinates
    renderer?.forEachPoint((_point, metadata) => {
      if (!isFinite(metadata.x) || !isFinite(metadata.y)) return
      const rect = {
        x: metadata.x,
        y: metadata.y,
        w: 1, h: 1
      }
      selectionTree.insert(rect, { datasetID: metadata.datasetID, caseID: metadata.caseID })
    })
  })
  return selectionTree
}

export const Background = forwardRef<SVGGElement | HTMLDivElement, IProps>((props, ref) => {
  const { marqueeState, rendererArray } = props,
    dataDisplayModel = useDataDisplayModelContext(),
    datasetsArray = dataDisplayModel.datasetsArray,
    datasetsMap: SelectionMap = useMemo(() => {
      const map: SelectionMap = {}
      datasetsArray.forEach((dataset) => {
        map[dataset.id] = {dataset, caseIDsToSelect: [], caseIDsToDeselect: []}
      })
      return map
    }, [datasetsArray]),
    layout = useDataDisplayLayout(),
    graphLayout = useGraphLayoutContext(),
    bgRef = ref as MutableRefObject<SVGGElement | null>,
    startX = useRef(0),
    startY = useRef(0),
    width = useRef(0),
    height = useRef(0),
    needsToClearSelection = useRef(false),
    selectionTree = useRef<RTree | null>(null),
    previousMarqueeRect = useRef<rTreeRect>(),
    isHoveredRef = useRef(false)

  const clearDatasetsMapArrays = useCallback(() => {
    Object.keys(datasetsMap).forEach((key) => {
      datasetsMap[key].caseIDsToSelect = []
      datasetsMap[key].caseIDsToDeselect = []
    })
  }, [datasetsMap])

  const onDragStart = useCallback((event: PointerEvent) => {
    appState.beginPerformance()
    // Skip point hit-testing for the duration of the drag: the marquee selects via the R-tree below,
    // so the pointer event system hit-testing every point sprite on each pointermove is pure overhead.
    rendererArray.forEach(renderer => renderer?.setPointsInteractive(false))
    selectionTree.current = prepareTree(rendererArray)
    // Event coordinates are window coordinates. To convert them to SVG coordinates, we need to subtract the
    // bounding rect of the SVG element.
    const bgRect = (bgRef.current as SVGGElement).getBoundingClientRect()
    startX.current = event.x - bgRect.left
    startY.current = event.y - bgRect.top
    width.current = 0
    height.current = 0
    marqueeState.setMarqueeRect({x: startX.current, y: startY.current, width: 0, height: 0})
    needsToClearSelection.current = !event.shiftKey
  }, [bgRef, marqueeState, rendererArray])

  const onDrag = useCallback((event: { dx: number; dy: number }) => {
    if ((event.dx === 0 && event.dy === 0) || datasetsArray.length === 0) return

    if (needsToClearSelection.current) {
      datasetsArray.forEach(data => {
        if (data.selection.size > 0) selectAllCases(data, false)
      })
      needsToClearSelection.current = false
    }

    previousMarqueeRect.current = rectNormalize(
      {x: startX.current, y: startY.current, w: width.current, h: height.current})
    width.current = width.current + event.dx
    height.current = height.current + event.dy
    // Visual clamp: when the Residual Plot's split is active, the marquee should not extend into
    // the lower region — there are no cases in the upper R-tree that live below the upper region,
    // so a marquee drawn there would visually promise a selection it can never make. useGraphLayoutContext
    // types the layout as GraphLayout; when Background is rendered in a non-graph tile (e.g. map)
    // the runtime layout doesn't have showLowerPlot and this evaluates falsy, so no clamp applies.
    // Coord system: marqueeState.y is plot-area-relative (0 = top of upper region), so the upper
    // region's bottom edge is at y = graphLayout.plotHeight.
    let clampedHeight = height.current
    if (graphLayout?.showLowerPlot) {
      const plotBottom = graphLayout.plotHeight
      const rectBottom = startY.current + height.current
      if (height.current > 0 && rectBottom > plotBottom) {
        // Math.max guards the case where the drag started at/below the upper region's bottom edge
        // (startY.current >= plotBottom): plotBottom - startY.current would be negative, and an SVG
        // rect with negative height is invalid.
        clampedHeight = Math.max(0, plotBottom - startY.current)
      }
    }
    marqueeState.setMarqueeRect({
      x: startX.current, y: startY.current,
      width: width.current,
      height: clampedHeight
    })
    const currentRect = rectNormalize({
      x: startX.current, y: startY.current,
      w: width.current,
      h: height.current
    })
    const newSelection = getCasesForDelta(selectionTree.current, currentRect, previousMarqueeRect.current)
    const newDeselection = getCasesForDelta(selectionTree.current, previousMarqueeRect.current, currentRect)
    // Stash the caseIDs to select and deselect for each dataset
    newSelection.forEach((caseObject: CaseObject) => {
      datasetsMap[caseObject.datasetID].caseIDsToSelect.push(caseObject.caseID)
    })
    newDeselection.forEach((caseObject: CaseObject) => {
      datasetsMap[caseObject.datasetID].caseIDsToDeselect.push(caseObject.caseID)
    })
    // Apply the selections and de-selections for each dataset. selectAndDeselectCases uses a
    // volatile selection + direct per-move delta notification (no applyModelChange).
    Object.values(datasetsMap).forEach((selectionSpec) => {
      const {dataset, caseIDsToSelect, caseIDsToDeselect} = selectionSpec
      selectAndDeselectCases(caseIDsToSelect, caseIDsToDeselect, dataset)
    })

    clearDatasetsMapArrays()
  }, [clearDatasetsMapArrays, datasetsArray, datasetsMap, graphLayout, marqueeState])

  const onDragEnd = useCallback(() => {
    const numCases = datasetsArray.reduce((sum, data) => sum + data.selection.size, 0)
    if (numCases > 0) {
      Logger.log(`marqueeSelection: ${numCases}`, { numCases }, "plot")
    }
    marqueeState.setMarqueeRect({x: 0, y: 0, width: 0, height: 0})
    selectionTree.current = null
    // Restore point hit-testing now that the drag is over (hover/click behavior).
    rendererArray.forEach(renderer => renderer?.setPointsInteractive(true))
    dataDisplayModel.setMarqueeMode("unclicked")
    appState.endPerformance()
  }, [dataDisplayModel, datasetsArray, marqueeState, rendererArray])

  useRendererPointerDownDeselect(rendererArray, dataDisplayModel)

  // If this component unmounts mid-drag (e.g. the tile is closed), onDragEnd may not run to restore
  // point hit-testing. Restore it on unmount so points aren't left non-interactive on a renderer that
  // outlives this marquee overlay. The ref keeps the cleanup on the current renderers while running
  // only on unmount.
  const rendererArrayRef = useRef(rendererArray)
  rendererArrayRef.current = rendererArray
  useEffect(() => {
    return () => rendererArrayRef.current.forEach(renderer => renderer?.setPointsInteractive(true))
  }, [])

  // Check if graph has at least one numeric axis that can be zoomed
  const hasNumericAxis = useCallback(() => {
    const xAxisModel = dataDisplayModel.getAxis('bottom')
    const yAxisModel = dataDisplayModel.getAxis('left')
    return isAnyNumericAxisModel(xAxisModel) || isAnyNumericAxisModel(yAxisModel)
  }, [dataDisplayModel])

  // Handle option-click zoom on plot background
  const handleOptionClickZoom = useCallback((event: PointerEvent) => {
    if (!event.altKey) return false
    if (!hasNumericAxis()) return false

    // Get plot bounds to convert coordinates
    const plotBounds = layout.computedBounds.plot
    if (!plotBounds) return false

    // Get the SVG bounding rect to convert from window coordinates to SVG coordinates
    if (!bgRef.current) return false
    const bgRect = bgRef.current.getBoundingClientRect()
    const svgX = event.x - bgRect.left - plotBounds.left
    const svgY = event.y - bgRect.top - plotBounds.top

    // Determine zoom factor
    const factor = event.shiftKey ? kZoomOutFactor : kZoomInFactor

    // Get tile model for undo/redo notifications
    const tileModel = getTileModel(dataDisplayModel)

    // Zoom the x-axis if numeric
    const xAxisModel = dataDisplayModel.getAxis('bottom')
    if (isAnyNumericAxisModel(xAxisModel)) {
      const xScale = graphLayout.getNumericScale('bottom')
      if (xScale) {
        const fixedValue = xScale.invert(svgX)
        zoomAxis(xAxisModel, fixedValue, factor, dataDisplayModel, tileModel)
      }
    }

    // Zoom the y-axis if numeric
    const yAxisModel = dataDisplayModel.getAxis('left')
    if (isAnyNumericAxisModel(yAxisModel)) {
      const yScale = graphLayout.getNumericScale('left')
      if (yScale) {
        const fixedValue = yScale.invert(svgY)
        zoomAxis(yAxisModel, fixedValue, factor, dataDisplayModel, tileModel)
      }
    }

    return true
  }, [bgRef, dataDisplayModel, graphLayout, hasNumericAxis, layout.computedBounds.plot])

  // Update cursor based on modifier keys
  const updateBackgroundCursor = useCallback((altKey: boolean, shiftKey: boolean) => {
    rendererArray.forEach(renderer => {
      if (!renderer?.canvas) return

      // Clear zoom classes first
      renderer.canvas.classList.remove('zoom-in', 'zoom-out')

      // Add appropriate zoom class if alt is held and there's a numeric axis
      if (altKey && hasNumericAxis()) {
        renderer.canvas.classList.add(shiftKey ? 'zoom-out' : 'zoom-in')
      }
    })
  }, [hasNumericAxis, rendererArray])

  // React to modifier key changes for immediate cursor feedback
  useEffect(() => {
    const disposer = reaction(
      () => [isKeyDown('Alt'), isKeyDown('Shift')] as const,
      ([altKey, shiftKey]) => {
        if (isHoveredRef.current) {
          updateBackgroundCursor(altKey, shiftKey)
        }
      }
    )
    return () => disposer()
  }, [updateBackgroundCursor])

  const renderBackground = useCallback(() => {
    if (!layout.computedBounds.plot) {
      return
    }
    const {left, top, width: plotWidth, height: plotHeight} = layout.computedBounds.plot,
      transform = `translate(${left}, ${top})`,
      {isTransparent, plotBackgroundColor = defaultBackgroundColor} = dataDisplayModel,
      bgColor = String(color(plotBackgroundColor)),
      darkBgColor = String(color(plotBackgroundColor)?.darker(0.2)),
      {numRows, numColumns} = layout,
      cellWidth = Math.max(0, plotWidth / numColumns),
      cellHeight = Math.max(0, plotHeight / numRows),
      row = (index: number) => Math.floor(index / numColumns),
      col = (index: number) => index % numColumns,
      groupElement = bgRef.current,
      fillOpacity = isTransparent ? 0
        : dataDisplayModel.plotBackgroundImage ? 0.001 : 1

    select(groupElement).selectAll('image').remove()
    select(groupElement).selectAll('rect').remove()
    if (dataDisplayModel.plotBackgroundImage) {
      let xCoord = left,
        imageWidth = plotWidth,
        yCoord = top,
        imageHeight = plotHeight
      if (dataDisplayModel.plotBackgroundImageLockInfo?.locked) {
        const xScale = graphLayout.getNumericScale('bottom'),
          yScale = graphLayout.getNumericScale('left')
        if (xScale) {
          xCoord = left + xScale(dataDisplayModel.plotBackgroundImageLockInfo.xAxisLowerBound)
          const right = left + xScale(dataDisplayModel.plotBackgroundImageLockInfo.xAxisUpperBound)
          imageWidth = right - xCoord
        }
        if (yScale) {
          yCoord = top + yScale(dataDisplayModel.plotBackgroundImageLockInfo.yAxisUpperBound)
          const bottom = top + yScale(dataDisplayModel.plotBackgroundImageLockInfo.yAxisLowerBound)
          imageHeight = bottom - yCoord
        }
      }
      select(groupElement).append('image')
        .attr("x", xCoord)
        .attr("y", yCoord)
        .attr("width", imageWidth)
        .attr("height", imageHeight)
        .attr("preserveAspectRatio", "none") // Stretch to fill the rectangle
        .attr("xlink:href", dataDisplayModel.plotBackgroundImage) // For older browsers
        .attr("href", dataDisplayModel.plotBackgroundImage) // For modern browsers
    }

    select(groupElement)
      .selectAll<SVGRectElement, number>('rect.plot-cell-background')
      .data(range(numRows * numColumns))
      .join('rect')
      .attr('class', 'plot-cell-background')
      .attr('data-testid', 'plot-cell-background')
      .attr('transform', transform)
      .attr('width', cellWidth)
      .attr('height', cellHeight)
      .attr('x', d => cellWidth * col(d))
      .attr('y', d => cellHeight * row(d))
      .style('fill', d => (row(d) + col(d)) % 2 === 0 ? bgColor : darkBgColor)
      .style('fill-opacity', fillOpacity)
      .on(BackgroundPassThroughEvent.PointerDown, pointerDownEvent => {
        // Handle option-click zoom first
        if (handleOptionClickZoom(pointerDownEvent)) {
          return // Don't start marquee selection if we zoomed
        }

        // Custom dragging implementation to avoid D3. Unfortunately, since we need to deal with events manually
        // dispatched from PixiJS canvas, we need to be very careful about the event handling. This implementation
        // allows us just to deal with pointerdown event being passed from canvas. pointermove and pointerup events
        // are attached to window directly (recommended way anyway).
        let draggingActive = true
        const prevXY = {x: pointerDownEvent.x, y: pointerDownEvent.y}
        onDragStart(pointerDownEvent)
        const onDragHandler = (onDragEvent: PointerEvent) => {
          if (draggingActive) {
            onDrag({dx: onDragEvent.x - prevXY.x, dy: onDragEvent.y - prevXY.y})
            prevXY.x = onDragEvent.x
            prevXY.y = onDragEvent.y
          }
        }
        const onDragEndHandler = () => {
          if (draggingActive) {
            draggingActive = false
            onDragEnd()
            window.removeEventListener("pointermove", onDragHandler)
            window.removeEventListener("pointerup", onDragEndHandler)
          }
        }
        window.addEventListener("pointermove", onDragHandler)
        window.addEventListener("pointerup", onDragEndHandler)
      })
      // Add mousemove listener for cursor updates and hover tracking
      // The Pixi canvas intercepts mouse events and re-dispatches them
      .on(BackgroundPassThroughEvent.MouseMove, (event: MouseEvent) => {
        isHoveredRef.current = true
        updateBackgroundCursor(event.altKey, event.shiftKey)
      })
      // Add mouseout listener to clear zoom cursor and hover tracking
      .on(BackgroundPassThroughEvent.MouseOut, () => {
        isHoveredRef.current = false
        select(groupElement).selectAll<SVGRectElement, number>('.plot-cell-background')
          .classed('zoom-in', false)
          .classed('zoom-out', false)
      })

    // Subtle gray backing so the Residual Plot region reads as its own subplot, matching the
    // darker shade used by the alternating checkerboard on categorical-split cells.
    // `pointer-events: none` keeps clicks passing through to the residual hit-rect and points
    // rendered above.
    if (graphLayout?.showLowerPlot) {
      const lowerBounds = graphLayout.getComputedBounds('lowerPlot')
      select(groupElement)
        .append('rect')
        .attr('class', 'residual-plot-background')
        .attr('data-testid', 'residual-plot-background')
        .attr('transform', transform)
        .attr('x', lowerBounds.left - left)
        .attr('y', lowerBounds.top - top)
        .attr('width', lowerBounds.width)
        .attr('height', lowerBounds.height)
        .style('fill', darkBgColor)
        .style('fill-opacity', fillOpacity)
        .style('pointer-events', 'none')
    }
  }, [layout, dataDisplayModel, bgRef, graphLayout, onDragStart, onDrag, onDragEnd, handleOptionClickZoom,
      updateBackgroundCursor])

  useEffect(function respondToAxisBoundsChange() {
    mstReaction(() => {
      let axisBounds:(number | undefined)[] = []
      if (dataDisplayModel?.plotBackgroundImageLockInfo?.locked) {
        const xAxisModel = dataDisplayModel.getAxis('bottom')
        if (isAnyNumericAxisModel(xAxisModel)) {
          axisBounds = axisBounds.concat([xAxisModel.max, xAxisModel.dynamicMax, xAxisModel.min, xAxisModel.dynamicMin])
        }
        const yAxisModel = dataDisplayModel.getAxis('left')
        if (isAnyNumericAxisModel(yAxisModel)) {
          axisBounds = axisBounds.concat([yAxisModel.max, yAxisModel.dynamicMax, yAxisModel.min, yAxisModel.dynamicMin])
        }
      }
      return axisBounds
    },
      () => {
        renderBackground()
      }, {name: "renderBackground", equals: comparer.structural, fireImmediately: true}, dataDisplayModel
    )
  }, [renderBackground, dataDisplayModel])

  useEffect(function respondToBackgroundChange() {
    mstReaction(
      () => [
        dataDisplayModel?.plotBackgroundImage,
        dataDisplayModel?.plotBackgroundColor,
        dataDisplayModel?.isTransparent
      ],
      () => {
        renderBackground()
      }, {name: "renderBackground", equals: comparer.structural, fireImmediately: true}, dataDisplayModel
    )
  }, [dataDisplayModel, renderBackground])

  // None of the reactions above observe layout.showLowerPlot or the derived lowerPlot bounds,
  // so toggling the Residual Plot needs an explicit trigger for the backing to redraw.
  useEffect(function respondToLowerPlotChange() {
    if (!graphLayout) return
    return mstReaction(
      () => [graphLayout.showLowerPlot, graphLayout.plotHeight, graphLayout.plotWidth] as const,
      () => {
        renderBackground()
      }, {name: "renderBackground.lowerPlot", equals: comparer.structural}, dataDisplayModel
    )
  }, [dataDisplayModel, graphLayout, renderBackground])

  return (
    <g className='background-group-element' ref={bgRef}/>
  )
})
Background.displayName = "Background"
